// ─────────────────────────────────────────────────────────────────────────────
// useComparisonTool.js — Comparison Tool data-flow hook
// ─────────────────────────────────────────────────────────────────────────────
//
// RESPONSIBILITIES
// ─────────────────
// 1. Owns all comparison tab state (status, data, error) in a single reducer.
// 2. Lazy-fetches each tab's data the first time it is activated — cached for
//    the modal's lifetime, never re-fetched unless explicitly retried.
// 3. Aborts in-flight requests on tab switch and unmount — keeping network
//    traffic minimal when the user clicks quickly between tabs.
// 4. Resets all tab data when the selected module (woNum) changes so stale
//    data from a previous module is never shown.
// 5. Exposes a stable interface to comparisonTool.js:
//      { activeTab, changeTab, tabData, retryTab, selectPeer,
//        scheduleViewMode, setScheduleViewMode, COMPARISON_TABS }
//
// HEAD-TO-HEAD PEER SELECTION
// ───────────────────────────
// The "schedule" tab (Head to Head) allows the user to pick which peer module
// to compare against via a dropdown rendered in HeadToHeadPanel.
//
// selectedPeerWoNum is stored here (not in the component) because changing it
// must trigger a re-fetch of the schedule tab — the same lifecycle that
// retryTab() uses.  The flow is:
//
//   1. User opens "Head to Head" tab → fetch fires with selectedPeerWoNum=""
//      → backend auto-selects the closest peer and returns availablePeers[].
//   2. User picks a peer from the dropdown → selectPeer(woNum) is called.
//   3. selectPeer stores the new woNum in selectedPeerWoNumRef (a ref, not
//      state, so it doesn't cause an extra render) and dispatches RESET_TAB
//      for "schedule".
//   4. The resetGen bump triggers Effect 2, which calls fetchComparison with
//      the new peerWoNum.  The result replaces the tab's data.
//
// selectedPeerWoNumRef is a ref rather than state because:
//   a. It must be readable inside the fetch effect's closure at call time
//      (not stale-captured), which useRef guarantees.
//   b. Changing it should NOT independently trigger a re-render or re-run
//      Effect 2 — only the RESET_TAB dispatch (which bumps resetGen) should
//      do that.  If it were state, React would run the effect twice: once for
//      the state change and once for resetGen, producing a double-fetch.
//
// JOB-COMPLETION VIEW-MODE SLIDER (NEW)
// ──────────────────────────────────────
// The Head to Head tab now has TWO views, toggled by a slider rendered inside
// HeadToHeadPanel — this hook does not add a fifth tab to COMPARISON_TABS,
// it adds a view-mode dimension WITHIN the existing "schedule" tab:
//
//   scheduleViewMode = "metrics"  → existing aggregate-metrics comparison
//                                   (fetched through the "schedule" tab slot,
//                                   unchanged from before this feature)
//   scheduleViewMode = "jobs"     → new per-job comparison (individual jobs,
//                                   who worked them, duration) fetched through
//                                   a SEPARATE internal tab slot, "jobs", so
//                                   its own status/data/error/resetGen don't
//                                   clobber the metrics view's cached result.
//                                   "jobs" is intentionally excluded from
//                                   COMPARISON_TABS (and from VALID_MODES,
//                                   which drives the tab nav bar) — it is
//                                   only ever reached via scheduleViewMode,
//                                   never via changeTab().
//
// WHY A SEPARATE TAB SLOT INSTEAD OF REUSING "schedule"
// ───────────────────────────────────────────────────────
// Metrics and jobs are different payload shapes from different dataServe
// modes (mode="schedule" vs mode="jobs"). Storing both under the same tabs.
// schedule slot would mean flipping the slider re-fetches and replaces data,
// discarding whichever view isn't currently showing — every slider flip would
// hit the network and show a loading spinner. A dedicated "jobs" slot caches
// independently, so flipping back and forth after the first fetch of each is
// instant, exactly like switching between two already-loaded top-level tabs.
//
// WHY mode="jobs" NEEDS A RESOLVED PEER, AND WHERE IT COMES FROM
// ─────────────────────────────────────────────────────────────
// Per dataServe.js/dataReceiver.js: mode="jobs" does not auto-select a peer
// (mode="schedule" owns that logic exclusively, so both views always agree on
// which module is being compared against). Effect 2 resolves the peer to send
// as: selectedPeerWoNumRef.current (the user's explicit pick) falling back to
// tabs.schedule.data?.peerWoNum (the peer schedule already resolved — user's
// pick or its own auto-selection). If neither is available yet (schedule
// hasn't succeeded), the jobs fetch is skipped for that render; it fires
// automatically once schedule's data arrives, because tabs.schedule.data is
// in Effect 2's dependency array.
//

// STATE MACHINE — per tab
// ────────────────────────
//   "idle"     Not yet fetched.  Fetch starts automatically when this tab
//              is activated.  Also the state after a retry reset.
//
//   "loading"  Fetch is in flight.  AbortController is live in inflightRef.
//
//   "success"  Data arrived.  Cached until woNum changes or retry is called.
//
//   "error"    Fetch failed (network, timeout, API error).  The user can call
//              retryTab() to reset to "idle" and trigger a fresh fetch.
//
// ABORT BEHAVIOUR
// ────────────────
// • Tab switch while loading  → changeTab() aborts the request and resets the
//   loading tab to "idle".  Switching back will restart the fetch cleanly.
//   Already-successful tabs are never aborted or reset on a tab switch.
//
// • woNum change              → Effect 1 dispatches RESET_ALL.  Effect 2's
//   cleanup aborts any in-flight request.  After the re-render (all tabs idle)
//   Effect 2 triggers a fresh fetch for the newly-active tab.
//
// • Component unmount         → Effect 2's cleanup aborts the in-flight request
//   so the resolved promise's .then() handler never calls dispatch on an
//   unmounted component.
//
// • Intentional abort (above) → fetchComparison returns { data:null, error:null }.
//   The .then() handler recognises this and exits without dispatching, leaving
//   state untouched for the reset/unmount path to handle cleanly.
//
// ── BUGFIX NOTE ──────────────────────────────────────────────────────────────
// A previous version keyed Effect 2's dependency array on `tabs[activeTab]
// .status`. That status is exactly what the effect's own FETCH_START dispatch
// changes (idle → loading) immediately after the fetch begins. React treats
// that as a dependency change, tears the effect down (aborting the brand-new
// request via the cleanup function), then re-runs it — and the re-run bails
// out because status is now "loading", not "idle". The result: every fetch
// self-cancelled within the same render pass and no tab ever loaded data,
// regardless of which tab was active.
//
// The fix: track a per-tab `resetGen` counter instead. It is bumped ONLY by
// explicit external resets (RESET_TAB from retry/selectPeer, RESET_ALL from a
// real woNum change when leaving a non-idle tab) — never by FETCH_START/
// SUCCESS/ERROR, which now carry the existing `resetGen` forward unchanged.
// Effect 2 depends on `resetGen` rather than `status`, so starting a fetch no
// longer triggers its own teardown. RESET_ALL additionally no-ops on tabs that
// are already idle (e.g. on mount) so it doesn't manufacture a spurious bump
// either.
//
// ── BUGFIX NOTE 2 (StrictMode double-invoke) ────────────────────────────────
// Even with the resetGen fix above, the active tab could still get stuck
// loading forever specifically on first mount, in development. React
// StrictMode double-invokes every effect on mount (setup → cleanup → setup
// again) before any dispatches from either pass are applied to state. Effect
// 1 fired RESET_ALL on both passes; the first was a no-op (tabs idle), but by
// the second pass Effect 2's first-pass FETCH_START was already queued ahead
// of it, so the second RESET_ALL was NOT a no-op — it reset the active tab
// back to idle and bumped its resetGen. That bump then mismatched what the
// second (real) fetch attempt had captured in its closure, so React tore it
// down and aborted it too, on top of the first request StrictMode's own
// synthetic cleanup had already killed. Result: two requests, both dead
// within milliseconds, active tab stuck in "loading" forever.
//
// The fix: `lastResetWoNumRef` makes Effect 1 a no-op unless woNum has
// genuinely changed since the last time it actually fired — not just
// "re-ran with the same value," which is exactly what StrictMode's
// synthetic remount does.
//
// USAGE
// ──────
//   import { useComparisonTool } from "./useComparisonTool";
//
//   const {
//     activeTab, changeTab, tabData, retryTab, selectPeer,
//     scheduleViewMode, setScheduleViewMode,
//   } = useComparisonTool(module);
//   // module is the prop passed down from moduleMetricsPage.js
//   // tabData["jobs"] holds the job-completion view's { status, data, error }
//   // — read it when scheduleViewMode === "jobs", same shape as any other tab.
// ─────────────────────────────────────────────────────────────────────────────

import { useReducer, useEffect, useRef, useCallback } from "react";
import { fetchComparison } from "../../utils/dataReceiver";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

/**
 * Tab definitions — exported so comparisonTool.js maps over them without
 * hardcoding strings.  The `id` values are the mode strings understood by
 * dataServe.js and dataReceiver.js.
 */
export const COMPARISON_TABS = [
  { id: "peer", label: "Item Number Comparison" },
  { id: "project", label: "Project Progress" },
  { id: "schedule", label: "Head to Head" },
  { id: "quality", label: "Quality Analysis" },
];

/** Valid mode ids — derived from COMPARISON_TABS to keep a single source of truth.
 *  These are the ids the visible tab nav bar cycles through via changeTab(). */
const VALID_MODES = COMPARISON_TABS.map((t) => t.id);

/**
 * All internally-tracked tab slots — VALID_MODES plus "jobs".
 * "jobs" deliberately has NO entry in COMPARISON_TABS: it is never reached via
 * changeTab(), only via the scheduleViewMode slider inside the "schedule" tab
 * (see JOB-COMPLETION VIEW-MODE SLIDER note above). It still needs its own
 * { status, data, error, resetGen } slot so its fetch lifecycle is identical
 * to every other tab's and it caches independently of the metrics view.
 */
const INTERNAL_TAB_IDS = [...VALID_MODES, "jobs"];

/**
 * Sentinel idle state shape — NOT a single shared object, since each tab now
 * carries its own `resetGen` counter. Spread this and add `resetGen`.
 */
const IDLE_BASE = Object.freeze({ status: "idle", data: null, error: null });

/** Factory for the initial tabs map — called on hook mount and woNum change. */
const makeIdleTabs = () =>
  Object.fromEntries(
    INTERNAL_TAB_IDS.map((id) => [id, { ...IDLE_BASE, resetGen: 0 }]),
  );

const INITIAL_STATE = {
  activeTab: "peer",
  tabs: makeIdleTabs(),
  // "metrics" | "jobs" — which view is showing inside the "schedule" tab.
  // Lives at the top level (not per-tab) since only "schedule" ever reads it.
  scheduleViewMode: "metrics",
};

// ─── REDUCER ──────────────────────────────────────────────────────────────────

/**
 * Pure reducer for all comparison tab state transitions.
 * All external state changes go through dispatch() — no direct setState calls
 * in the hook body, keeping the state machine auditable in one place.
 *
 * Actions
 * ────────
 *   CHANGE_TAB    { tab }           Set the active tab.  No-op if same tab.
 *   FETCH_START   { mode }          Mark a tab as loading.
 *   FETCH_SUCCESS { mode, data }    Store successful payload.
 *   FETCH_ERROR   { mode, error }   Store structured error from dataReceiver.
 *   RESET_TAB     { mode }          Reset one tab to idle (retry / peer-select).
 *                                   Always bumps that tab's resetGen. `mode`
 *                                   may be "jobs" as well as any COMPARISON_TABS id.
 *   RESET_ALL     (no payload)      Reset every tab (including "jobs") to idle
 *                                   (woNum change). Bumps resetGen only for
 *                                   tabs that weren't already idle, so a
 *                                   mount-time reset of an already-idle tab is
 *                                   a true no-op. Also resets scheduleViewMode
 *                                   back to "metrics" for the new module.
 *   SET_SCHEDULE_VIEW_MODE { viewMode }
 *                                   Switch the Head to Head tab between
 *                                   "metrics" and "jobs". No-op if unchanged
 *                                   or not one of those two values.
 *
 * NOTE on resetGen: FETCH_START / FETCH_SUCCESS / FETCH_ERROR deliberately
 * carry the tab's existing resetGen forward unchanged. resetGen only moves
 * when something *outside* the normal fetch lifecycle decides this tab needs
 * a fresh attempt. See the BUGFIX NOTE above for why this matters.
 */
function comparisonReducer(state, action) {
  switch (action.type) {
    case "CHANGE_TAB":
      if (action.tab === state.activeTab) return state;
      return { ...state, activeTab: action.tab };

    case "FETCH_START":
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.mode]: {
            ...state.tabs[action.mode],
            status: "loading",
            data: null,
            error: null,
            // resetGen intentionally NOT changed — see BUGFIX NOTE
          },
        },
      };

    case "FETCH_SUCCESS":
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.mode]: {
            ...state.tabs[action.mode],
            status: "success",
            data: action.data,
            error: null,
            // resetGen intentionally NOT changed
          },
        },
      };

    case "FETCH_ERROR":
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.mode]: {
            ...state.tabs[action.mode],
            status: "error",
            data: null,
            error: action.error,
            // resetGen intentionally NOT changed
          },
        },
      };

    case "RESET_TAB":
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.mode]: {
            ...IDLE_BASE,
            resetGen: (state.tabs[action.mode]?.resetGen ?? 0) + 1,
          },
        },
      };

    case "RESET_ALL": {
      // Bump resetGen only for tabs that are NOT already idle.
      // A tab that is already idle needs no reset — bumping its resetGen
      // would cause Effect 2 to fire an unnecessary extra fetch on mount.
      // Loops over INTERNAL_TAB_IDS (not VALID_MODES) so the "jobs" slot is
      // reset alongside the four visible tabs on every module change.
      const newTabs = {};
      for (const id of INTERNAL_TAB_IDS) {
        const tab = state.tabs[id];
        const alreadyIdle =
          tab.status === "idle" && tab.data === null && tab.error === null;
        newTabs[id] = alreadyIdle
          ? tab
          : { ...IDLE_BASE, resetGen: tab.resetGen + 1 };
      }
      // New module → job-completion slider goes back to its default position.
      return { ...state, tabs: newTabs, scheduleViewMode: "metrics" };
    }

    case "SET_SCHEDULE_VIEW_MODE":
      if (action.viewMode !== "metrics" && action.viewMode !== "jobs") {
        return state;
      }
      if (action.viewMode === state.scheduleViewMode) return state;
      return { ...state, scheduleViewMode: action.viewMode };

    default:
      return state;
  }
}

// ─── HOOK ──────────────────────────────────────────────────────────────────────

/**
 * useComparisonTool — drives all four comparison tabs (plus the internal
 * "jobs" slot used by the Head to Head job-completion slider) for a given
 * module.
 *
 * @param {{ workOrder: string }|null} module
 *   The selected module from moduleMetricsPage.js.
 *   Only `workOrder` is used by this hook; the component passes the full
 *   object down to each panel for display.
 *
 * @returns {{
 *   activeTab:           string,
 *   changeTab:           (newTab: string) => void,
 *   tabData:             Record<string, { status, data, error, resetGen }>,
 *   retryTab:            (mode?: string) => void,
 *   selectPeer:          (peerWoNum: string) => void,
 *   scheduleViewMode:    "metrics" | "jobs",
 *   setScheduleViewMode: (viewMode: "metrics" | "jobs") => void,
 * }}
 */
export function useComparisonTool(module) {
  const woNum = module?.workOrder ?? null;

  const [{ activeTab, tabs, scheduleViewMode }, dispatch] = useReducer(
    comparisonReducer,
    INITIAL_STATE,
  );

  // Tracks the in-flight fetch so changeTab/selectPeer can abort it cleanly.
  const inflightRef = useRef({ controller: null, mode: null });

  // Guards Effect 1 against StrictMode's synthetic double-invoke — see BUGFIX NOTE 2.
  const lastResetWoNumRef = useRef(null);

  // Stores the user-selected peer WO# for the head-to-head tab.
  // A ref (not state) so the fetch closure reads the value current at call
  // time without stale capture, and so changing it doesn't independently
  // re-run Effect 2 (only the RESET_TAB resetGen bump does that).
  const selectedPeerWoNumRef = useRef("");

  // ── Effect 1: Reset all tabs when the module changes ──────────────────────
  //
  // Guards against StrictMode's synthetic remount by checking whether woNum
  // actually changed since the last genuine reset.  On a real module change
  // (including first mount) it also clears the stored peer selection so the
  // new module's head-to-head tab starts with auto-selection.
  useEffect(() => {
    if (!woNum) return;
    if (lastResetWoNumRef.current === woNum) return;
    lastResetWoNumRef.current = woNum;
    selectedPeerWoNumRef.current = ""; // clear peer selection for new module
    dispatch({ type: "RESET_ALL" });
  }, [woNum]);

  // ── Effect 2: Fetch data for the active (effective) tab when it is idle ───
  //
  // "Effective mode" is almost always just activeTab — EXCEPT when the user
  // is on the "schedule" tab with scheduleViewMode="jobs", in which case the
  // fetch/cache target is the internal "jobs" slot instead (see JOB-
  // COMPLETION VIEW-MODE SLIDER note at the top of this file). Everything
  // below operates on effectiveMode, not activeTab directly, so the jobs
  // slider reuses the exact same fetch/abort/cache lifecycle as any tab.
  //
  // Runs when activeTab, scheduleViewMode, woNum, the effective tab's
  // resetGen, or (for the jobs case) tabs.schedule.data changes.
  // The status check ensures:
  //   • Loading/success/error tabs (or the jobs slot) are never re-fetched.
  //   • Idle tabs are fetched exactly once (first activation, after retry,
  //     after peer select, or after switching the view-mode slider).
  //
  // For "schedule", selectedPeerWoNumRef.current is read at fetch time to
  // pass the user's chosen peer (or "" for auto-select). For "jobs", a peer
  // is REQUIRED (dataServe returns 400 without one) — see the JOB-COMPLETION
  // note above for why it falls back to tabs.schedule.data?.peerWoNum, and
  // why the fetch is simply skipped (not errored) until that's available.
  //
  // Dep array note: tabs[effectiveMode].resetGen — NOT status — is the dep
  // here. See the BUGFIX NOTE at the top for the full explanation. Both
  // tabs.schedule.resetGen and tabs.jobs.resetGen are listed unconditionally
  // (rather than a single dynamic tabs[effectiveMode].resetGen) because React
  // hook dependency arrays must be a fixed-size, statically-known list.
  useEffect(() => {
    if (!woNum) return;

    const effectiveMode =
      activeTab === "schedule" && scheduleViewMode === "jobs"
        ? "jobs"
        : activeTab;

    if (tabs[effectiveMode].status !== "idle") return;

    // Capture the peer selection / resolve the required peer at the moment
    // this effect fires. For "peer"/"project"/"quality" this stays undefined
    // and fetchComparison/dataServe both ignore it harmlessly.
    let peerWoNum;
    if (effectiveMode === "schedule") {
      peerWoNum = selectedPeerWoNumRef.current;
    } else if (effectiveMode === "jobs") {
      peerWoNum =
        selectedPeerWoNumRef.current || tabs.schedule.data?.peerWoNum || "";
      // No resolved peer yet — the schedule fetch hasn't succeeded. Bail out
      // without dispatching; this effect re-runs automatically once
      // tabs.schedule.data changes (it's in the dep array below) and picks
      // the peer up then.
      if (!peerWoNum) return;
    } else {
      peerWoNum = undefined;
    }

    const controller = new AbortController();
    inflightRef.current = { controller, mode: effectiveMode };

    dispatch({ type: "FETCH_START", mode: effectiveMode });

    fetchComparison(woNum, effectiveMode, controller.signal, peerWoNum).then(
      ({ data, error }) => {
        // { data: null, error: null } is the intentional-cancel sentinel.
        // Fires when the controller above was aborted by the cleanup below,
        // by changeTab(), or by selectPeer(). In all cases the calling code
        // has already reset the tab state — do NOT dispatch here.
        if (data === null && error === null) return;

        // Clear the inflight ref if this promise's controller is still current.
        if (inflightRef.current?.controller === controller) {
          inflightRef.current = { controller: null, mode: null };
        }

        dispatch(
          error
            ? { type: "FETCH_ERROR", mode: effectiveMode, error }
            : { type: "FETCH_SUCCESS", mode: effectiveMode, data },
        );
      },
    );

    return () => {
      if (inflightRef.current?.controller === controller) {
        inflightRef.current.controller.abort();
        inflightRef.current = { controller: null, mode: null };
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    scheduleViewMode,
    woNum,
    tabs.schedule.resetGen,
    tabs.jobs.resetGen,
    tabs.schedule.data,
  ]);
  //  ↑ resetGen deps are primitive, immune to this effect's own dispatches.
  //    tabs.schedule.data is an object reference dep, included so the jobs
  //    fetch can pick up the peer once schedule resolves it; it only changes
  //    on schedule's own FETCH_SUCCESS/ERROR/RESET, never from this effect's
  //    jobs-branch dispatches, so it cannot cause a self-triggered loop.
  //    ESLint may warn about object/property access in deps; the disable
  //    comment above suppresses that specific warning.

  // ── changeTab — public API for the tab navigation bar ─────────────────────
  //
  // Wraps the CHANGE_TAB dispatch with abort-and-reset logic: if the current
  // tab is mid-fetch its request is aborted and the tab is reset to idle so
  // switching back restarts the fetch cleanly. Already-loaded tabs are left
  // untouched.
  //
  // "leavingEffectiveMode" mirrors Effect 2's effectiveMode calculation: if
  // the user is leaving "schedule" while scheduleViewMode="jobs", the tab
  // that's actually fetching/cached is the internal "jobs" slot, not
  // "schedule" itself — so that's the one to check/abort/reset.
  const changeTab = useCallback(
    (newTab) => {
      if (newTab === activeTab) return;
      if (!VALID_MODES.includes(newTab)) return;

      const leavingEffectiveMode =
        activeTab === "schedule" && scheduleViewMode === "jobs"
          ? "jobs"
          : activeTab;

      if (tabs[leavingEffectiveMode].status === "loading") {
        if (inflightRef.current?.controller) {
          inflightRef.current.controller.abort();
          inflightRef.current = { controller: null, mode: null };
        }
        dispatch({ type: "RESET_TAB", mode: leavingEffectiveMode });
      }

      dispatch({ type: "CHANGE_TAB", tab: newTab });
    },
    [activeTab, scheduleViewMode, tabs],
  );

  // ── retryTab — public API for the ErrorState retry button ─────────────────
  //
  // Resets the target tab to idle, which triggers Effect 2 to start a fresh
  // fetch. Defaults to the currently active EFFECTIVE tab if no mode is
  // specified — i.e. "jobs" rather than "schedule" when the job-completion
  // view is the one currently showing an error.
  const retryTab = useCallback(
    (mode) => {
      const defaultMode =
        activeTab === "schedule" && scheduleViewMode === "jobs"
          ? "jobs"
          : activeTab;
      const targetMode = mode ?? defaultMode;
      if (!INTERNAL_TAB_IDS.includes(targetMode)) return;

      if (inflightRef.current?.mode === targetMode) {
        inflightRef.current.controller?.abort();
        inflightRef.current = { controller: null, mode: null };
      }

      dispatch({ type: "RESET_TAB", mode: targetMode });
    },
    [activeTab, scheduleViewMode],
  );

  // ── selectPeer — public API for the head-to-head peer dropdown ────────────
  //
  // Called by HeadToHeadPanel when the user picks a different peer module.
  // Stores the new peer WO# in the ref (read by Effect 2 at fetch time), then
  // resets BOTH head-to-head slots — "schedule" (metrics view) and "jobs"
  // (job-completion view) — to idle, since the old peer invalidates whichever
  // of the two was already fetched. Effect 2 re-fetches whichever one is
  // currently the effective mode; the other stays idle until the slider is
  // flipped to it, at which point it fetches fresh with the new peer too.
  //
  // If either slot is currently loading, the in-flight request is aborted
  // first so it doesn't race against the new one.
  const selectPeer = useCallback(
    (peerWoNum) => {
      // Store new peer before the reset so Effect 2 reads it at fetch time.
      selectedPeerWoNumRef.current = peerWoNum ?? "";

      // Abort whichever head-to-head fetch is currently in flight.
      if (
        inflightRef.current?.mode === "schedule" ||
        inflightRef.current?.mode === "jobs"
      ) {
        inflightRef.current.controller?.abort();
        inflightRef.current = { controller: null, mode: null };
      }

      dispatch({ type: "RESET_TAB", mode: "schedule" });
      dispatch({ type: "RESET_TAB", mode: "jobs" });

      // If the schedule tab is not currently active, switch to it so the
      // user immediately sees the new comparison rather than needing to
      // click the tab manually after selecting from a different panel.
      if (activeTab !== "schedule") {
        dispatch({ type: "CHANGE_TAB", tab: "schedule" });
      }
    },
    [activeTab],
  );

  // ── setScheduleViewMode — public API for the metrics/jobs slider ──────────
  //
  // Switches which view is showing inside the "schedule" tab. Does not reset
  // or re-fetch anything itself — the target slot ("schedule" or "jobs")
  // keeps whatever cached state it already has (idle → Effect 2 fetches it;
  // success → shown instantly with no network call; error → the ErrorState
  // for that slot is shown, retryable via retryTab()).
  const setScheduleViewMode = useCallback((viewMode) => {
    dispatch({ type: "SET_SCHEDULE_VIEW_MODE", viewMode });
  }, []);

  // ── Return value ───────────────────────────────────────────────────────────
  return {
    /** The id of the currently visible tab, e.g. "peer". */
    activeTab,

    /**
     * Call this instead of setting activeTab directly — it handles aborting
     * any in-flight request for the tab being left before switching.
     * @param {string} newTab  One of COMPARISON_TABS[].id
     */
    changeTab,

    /**
     * Map of all tracked tabs' fetch state, including the internal "jobs"
     * slot used by the Head to Head job-completion slider.
     * Access as tabData["peer"], tabData["schedule"], tabData["jobs"], etc.
     * Each entry: { status, data, error } (plus an internal `resetGen` field
     * used by this hook — safe to ignore in the component).
     */
    tabData: tabs,

    /**
     * Resets a tab to idle and re-triggers its fetch. Pass a mode id
     * ("peer" | "project" | "schedule" | "quality" | "jobs") to retry a
     * specific one, or call with no arguments to retry whichever is
     * currently the active EFFECTIVE tab (accounting for scheduleViewMode).
     * @param {string} [mode]
     */
    retryTab,

    /**
     * Select a peer module for the Head to Head tab. Stores the peer WO#,
     * aborts any in-flight schedule/jobs fetch, resets both head-to-head
     * slots to idle (so whichever is currently showing re-fetches with the
     * new peer), and switches to the schedule tab if it is not already active.
     * @param {string} peerWoNum  Work order number of the desired peer module.
     */
    selectPeer,

    /**
     * Which view is currently showing inside the Head to Head tab.
     * "metrics" = existing aggregate comparison; "jobs" = new per-job
     * comparison. Read this to drive the slider's visual state.
     */
    scheduleViewMode,

    /**
     * Flips the Head to Head slider between "metrics" and "jobs". No-op for
     * any other value. Does not force a re-fetch — see comment above.
     * @param {"metrics"|"jobs"} viewMode
     */
    setScheduleViewMode,
  };
}
